import React, {FC, Fragment, ReactNode} from 'react';
import styled from 'styled-components';

import {
  KeyboardActionsEnum,
  KeyboardMapping,
} from '../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {getHasRepeatFormatting} from '../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {useKeyboardContext} from '../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcutsFormatter} from '../../../libs/shared/ui/src/components/keyboard/keyboardShortcutFormatter';
import {fonts, fontSizes} from '../../../libs/shared/ui/src/styles/fontStyles';
import {useAppSelector} from '../../helpers/appReduxHelpers';
import {getHasAISummarizeFeature, getIsAiSummarizeEnabled} from '../../store/data/company/companySelectors';

/*
 * Style.
 */

const StyledContainerDiv = styled.div`
  overflow: scroll;
`;

const StyledSubtitleDiv = styled.div`
  font-size: ${fontSizes.medium};
  padding: 16px;
  text-align: center;
  font-style: italic;
  vertical-align: center;
  flex-grow: 0;
`;

const StyledKeyboardButton = styled.div`
  font-family: ${fonts.monospace};
  font-size: ${fontSizes.tiny};
  padding: 4px 5px;
  font-style: normal;
  border: 1px solid ${(p) => p.theme.greys.shade50};
  border-radius: 3px;
  box-shadow: inset 0 -1px 0 ${(p) => p.theme.greys.shade40};
  color: ${(p) => p.theme.greys.shade80};
  display: inline-block;
  text-transform: capitalize;
  vertical-align: center;
`;

const StyledShortcutTitle = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  margin-right: 8px;
`;

const StyledShortcutKey = styled.div`
  flex-shrink: 0;
  text-align: right;
`;

const StyledHeaderDiv = styled.div`
  font-size: ${fontSizes.medium};
  padding: 16px;
  text-align: left;
  flex-grow: 1;
  font-weight: 700;
  text-transform: uppercase;
  color: ${(p) => p.theme.greys.shade50};
  line-height: 2;
`;

const StyledShortcutRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  font-size: ${fontSizes.small};
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  margin: 0px 16px;
  padding: 8px 0px;
  text-align: left;
  align-items: center;
`;

/*
 * Component.
 */

export const KeyboardHelpContent: FC = () => {
  const {mapping} = useKeyboardContext();
  const hasRepeatFormatting = useAppSelector(getHasRepeatFormatting);

  const hasSummarizeFeature = useAppSelector(getHasAISummarizeFeature);
  const isAiSummarizeEnabled = useAppSelector(getIsAiSummarizeEnabled);
  const shouldDisplayConversationSummarize = hasSummarizeFeature && isAiSummarizeEnabled;

  return (
    <StyledContainerDiv>
      <StyledSubtitleDiv>
        Press <StyledKeyboardButton>?</StyledKeyboardButton> to show this again
      </StyledSubtitleDiv>
      {makeData({hasRepeatFormatting}).map((section, index) =>
        renderSection(section, index, mapping, shouldDisplayConversationSummarize),
      )}
    </StyledContainerDiv>
  );
};

/*
 * Section.
 */

function renderSection(
  section: ShortcutsSection,
  index: number,
  mapping: KeyboardMapping,
  shouldDisplayConversationSummarize: boolean,
) {
  const rows = section.keys
    .filter(
      (key) =>
        key.action !== KeyboardActionsEnum.TOGGLE_EXPANDED_CONVERSATION_SUMMARY ||
        shouldDisplayConversationSummarize,
    )
    .map((key) => {
      if (mapping[key.action].length === 0) {
        return null;
      }
      return (
        <StyledShortcutRow key={key.action}>
          <StyledShortcutTitle>{key.title}</StyledShortcutTitle>
          <StyledShortcutKey>
            <KeyboardShortcutsFormatter action={key.action} />
          </StyledShortcutKey>
        </StyledShortcutRow>
      );
    })
    .filter((row) => row != null);
  if (rows.length > 0) {
    return (
      <Fragment key={index}>
        <StyledHeaderDiv>{section.title}</StyledHeaderDiv>
        <div>{rows}</div>
      </Fragment>
    );
  }
  return null;
}

/*
 * Data.
 */

interface ShortcutsOptions {
  hasRepeatFormatting: boolean;
}

interface ShortcutsSection {
  title: ReactNode;
  keys: ReadonlyArray<{
    action: KeyboardActionsEnum;
    title: ReactNode;
  }>;
}

const makeData = (options: ShortcutsOptions): ReadonlyArray<ShortcutsSection> => [
  {
    title: <Trans id="shortcuts-sectiontitle-navigation">Navigation</Trans>,
    keys: [
      {
        action: KeyboardActionsEnum.GO_TO_INBOX,
        title: <Trans id="shortcuts-description-gotoinbox">Go to inbox</Trans>,
      },
      {
        action: KeyboardActionsEnum.HISTORY_BACK,
        title: <Trans id="shortcuts-description-historyback">Back in History</Trans>,
      },
      {
        action: KeyboardActionsEnum.HISTORY_FORWARD,
        title: <Trans id="shortcuts-description-historyforward">Forward in History</Trans>,
      },
      {
        action: KeyboardActionsEnum.SEARCH,
        title: <Trans id="shortcuts-description-search">Search</Trans>,
      },
      {
        action: KeyboardActionsEnum.SELECT_ALL,
        title: <Trans id="shortcuts-description-selectall">Select all conversations</Trans>,
      },
      {
        action: KeyboardActionsEnum.GO_TO_CONVERSATION,
        title: <Trans id="shortcuts-description-gotoconversation">Go to conversation</Trans>,
      },
      {
        action: KeyboardActionsEnum.TOGGLE_SIDE_PANEL,
        title: <Trans id="shortcuts-description-togglesidepanel">Open plugins panel</Trans>,
      },
      {
        action: KeyboardActionsEnum.OPEN_PALETTE,
        title: <Trans id="shortcuts-description-openpalette">Open quick switcher</Trans>,
      },
    ],
  },
  {
    title: <Trans id="shortcuts-sectiontitle-conversation">Conversation Actions</Trans>,
    keys: [
      {
        action: KeyboardActionsEnum.SUBSCRIBE,
        title: <Trans id="shortcuts-description-unsubscribeorsubscribe">Unsubscribe / subscribe</Trans>,
      },
      {
        action: KeyboardActionsEnum.ARCHIVE,
        title: <Trans id="shortcuts-description-archive">Archive / unarchive</Trans>,
      },
      {
        action: KeyboardActionsEnum.TRASH,
        title: <Trans id="shortcuts-description-trash">Delete</Trans>,
      },
      {
        action: KeyboardActionsEnum.SPAM,
        title: <Trans id="shortcuts-description-spam">Mark as spam</Trans>,
      },
      {
        action: KeyboardActionsEnum.EXPAND_ALL,
        title: <Trans id="shortcuts-description-expandall">Expand all messages</Trans>,
      },
      {
        action: KeyboardActionsEnum.COLLAPSE_ALL,
        title: <Trans id="shortcuts-description-collapseall">Collapse all messages</Trans>,
      },
      {
        action: KeyboardActionsEnum.ASSIGN_TO_SELF,
        title: <Trans id="shortcuts-description-assigntoself">Assign to yourself</Trans>,
      },
      {
        action: KeyboardActionsEnum.ASSIGN,
        title: <Trans id="shortcuts-description-assign">Assign to a teammate</Trans>,
      },
      {
        action: KeyboardActionsEnum.SNOOZE,
        title: <Trans id="shortcuts-description-snooze">Snooze</Trans>,
      },
      {
        action: KeyboardActionsEnum.MOVE_TO_INBOX,
        title: <Trans id="shortcuts-description-movetoinbox">Move to other inbox</Trans>,
      },
      {
        action: KeyboardActionsEnum.TAG,
        title: <Trans id="shortcuts-description-tag">Add a tag</Trans>,
      },
      {
        action: KeyboardActionsEnum.FOCUS_COMMENT,
        title: <Trans id="shortcuts-description-comment">Go to comment bar</Trans>,
      },
      {
        action: KeyboardActionsEnum.REPLY,
        title: <Trans id="shortcuts-description-reply">Reply</Trans>,
      },
      {
        action: KeyboardActionsEnum.REPLY_IN_NEW_WINDOW,
        title: <Trans id="shortcuts-description-replynewwindow">Reply in a separate window</Trans>,
      },
      {
        action: KeyboardActionsEnum.REPLY_ALL,
        title: <Trans id="shortcuts-description-replyall">Reply all</Trans>,
      },
      {
        action: KeyboardActionsEnum.REPLY_ALL_IN_NEW_WINDOW,
        title: <Trans id="shortcuts-description-replyallnewwindow">Reply all in a separate window</Trans>,
      },
      {
        action: KeyboardActionsEnum.FORWARD,
        title: <Trans id="shortcuts-description-forward">Forward</Trans>,
      },
      {
        action: KeyboardActionsEnum.REPLY_INTRO,
        title: <Trans id="shortcuts-description-replyintro">Intro</Trans>,
      },
      {
        action: KeyboardActionsEnum.TOGGLE_EXPANDED_CONVERSATION_SUMMARY,
        title: (
          <Trans id="shortcuts-description-conversation-summary">
            Generate or toggle conversation summary
          </Trans>
        ),
      },
      {
        action: KeyboardActionsEnum.INFORMATION,
        title: <Trans id="shortcuts-description-information">Show conversation details</Trans>,
      },
      {
        action: KeyboardActionsEnum.STAR,
        title: <Trans id="shortcuts-description-starunstar">Star/Unstar</Trans>,
      },
    ],
  },
  {
    title: <Trans id="shortcuts-sectiontitle-composer">Composer Actions</Trans>,
    keys: [
      {
        action: KeyboardActionsEnum.COMPOSE,
        title: <Trans id="shortcuts-description-compose">Compose a new message</Trans>,
      },
      {
        action: KeyboardActionsEnum.COMPOSE_DISCUSSION,
        title: <Trans id="shortcuts-description-composediscussion">Start a new discussion</Trans>,
      },
      {
        action: KeyboardActionsEnum.EDITOR_CC,
        title: <Trans id="shortcuts-description-editorCC">Toggle CC field</Trans>,
      },
      {
        action: KeyboardActionsEnum.EDITOR_BCC,
        title: <Trans id="shortcuts-description-editorBCC">Toggle BCC field</Trans>,
      },
      {
        action: KeyboardActionsEnum.SEND_AND_DONE,
        title: <Trans id="shortcuts-description-sendandarchive">Send and archive</Trans>,
      },
      {
        action: KeyboardActionsEnum.SEND,
        title: <Trans id="shortcuts-description-send">Send</Trans>,
      },
      {
        action: KeyboardActionsEnum.REMIND,
        title: <Trans id="shortcuts-description-remind">Open reminder</Trans>,
      },
      {
        action: KeyboardActionsEnum.EDITOR_ANSWERS,
        title: <Trans id="shortcuts-description-editoranswers">Open message templates</Trans>,
      },
      {
        action: KeyboardActionsEnum.QUICK_RESPONSE_1,
        title: <Trans id="shortcuts-description-editorresponse1">Open message template 1</Trans>,
      },
      {
        action: KeyboardActionsEnum.QUICK_RESPONSE_2,
        title: <Trans id="shortcuts-description-editorresponse2">Open message template 2</Trans>,
      },
      {
        action: KeyboardActionsEnum.QUICK_RESPONSE_3,
        title: <Trans id="shortcuts-description-editorresponse3">Open message template 3</Trans>,
      },
      {
        action: KeyboardActionsEnum.QUICK_RESPONSE_4,
        title: <Trans id="shortcuts-description-editorresponse4">Open message template 4</Trans>,
      },
      {
        action: KeyboardActionsEnum.QUICK_RESPONSE_5,
        title: <Trans id="shortcuts-description-editorresponse5">Open message template 5</Trans>,
      },
      {
        action: KeyboardActionsEnum.EDITOR_INDENT,
        title: <Trans id="shortcuts-description-editorindent">Indent</Trans>,
      },
      {
        action: KeyboardActionsEnum.EDITOR_OUTDENT,
        title: <Trans id="shortcuts-description-editoroutdent">Outdent</Trans>,
      },
      {
        action: KeyboardActionsEnum.EDITOR_INSERT_LINK,
        title: <Trans id="shortcuts-description-editorinsertlink">Insert link</Trans>,
      },
      {
        action: KeyboardActionsEnum.UNDO,
        title: <Trans id="shortcuts-description-undo">Undo</Trans>,
      },
      {
        action: KeyboardActionsEnum.REDO,
        title: <Trans id="shortcuts-description-redo">Redo</Trans>,
      },
      ...(options.hasRepeatFormatting
        ? [
            {
              action: KeyboardActionsEnum.REDO,
              title: <Trans id="shortcuts-description-redo">Repeat formatting</Trans>,
            },
          ]
        : []),
      {
        action: KeyboardActionsEnum.EDITOR_BOLD,
        title: <Trans id="shortcuts-description-editorbold">Bold</Trans>,
      },
      {
        action: KeyboardActionsEnum.EDITOR_ITALIC,
        title: <Trans id="shortcuts-description-editoritalic">Italic</Trans>,
      },
      {
        action: KeyboardActionsEnum.EDITOR_UNDERLINE,
        title: <Trans id="shortcuts-description-editorunderline">Underline</Trans>,
      },
    ],
  },
  {
    title: <Trans id="shortcuts-sectiontitle-calendar">Calendar</Trans>,
    keys: [
      {
        action: KeyboardActionsEnum.SWITCH_TO_CALENDAR,
        title: <Trans id="shortcuts-description-switchtocalendar">Switch to full-screen calendar</Trans>,
      },
      {
        action: KeyboardActionsEnum.COMPOSE,
        title: <Trans id="shortcuts-description-calendarcompose">Create a new event</Trans>,
      },
      {
        action: KeyboardActionsEnum.CALENDAR_EDIT_EVENT,
        title: <Trans id="shortcuts-description-calendareditevent">Edit event</Trans>,
      },
      {
        action: KeyboardActionsEnum.CALENDAR_DELETE_EVENT,
        title: <Trans id="shortcuts-description-calendardeleteevent">Delete event</Trans>,
      },
      {
        action: KeyboardActionsEnum.CLOSE,
        title: <Trans id="shortcuts-description-calendarclose">Close event popover</Trans>,
      },
      {
        action: KeyboardActionsEnum.CALENDAR_GO_TO_TODAY,
        title: <Trans id="shortcuts-description-gototoday">Go to Today</Trans>,
      },
      {
        action: KeyboardActionsEnum.CALENDAR_PREVIOUS,
        title: <Trans id="shortcuts-description-calendarprevious">Previous week/month</Trans>,
      },
      {
        action: KeyboardActionsEnum.CALENDAR_NEXT,
        title: <Trans id="shortcuts-description-calendarnext">Next week/month</Trans>,
      },
      {
        action: KeyboardActionsEnum.CALENDAR_WEEK_VIEW,
        title: <Trans id="shortcuts-description-weekview">Week view</Trans>,
      },
      {
        action: KeyboardActionsEnum.CALENDAR_MONTH_VIEW,
        title: <Trans id="shortcuts-description-monthview">Month view</Trans>,
      },
    ],
  },
];
